/**
 * Account My Subscription Page
 * Mirrors the native app's SubscriptionManageScreen
 * Shows plan details, usage, payment method, features, and actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, Gift, AlertCircle, ArrowUpCircle, XCircle,
  Check, Loader2, HelpCircle, Mail, ChevronUp
} from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import { API_BASE_URL } from '../../config/api';

const SUPPORT_EMAIL = 'support@ezdaily.com';

// Feature short key -> display label
const featureLabels = {
  acm: 'AI conversations/month',
  ame: 'AI memory',
  fl: 'Featured listings',
  ps: 'Priority support',
  ad: 'Analytics dashboard',
  vle: 'Voice listing creation',
  lfa: 'Late fee automation',
  er: 'Export reports',
  cet: 'Custom email templates',
  cb: 'Custom branding',
  aa: 'API access',
  tml: 'Team members',
  dam: 'Dedicated account manager',
  rr: 'Automated rent reminders',
  rrs: 'Roommate rent splitting',
  mioc: 'Move-in/out photo checklists',
  lal: 'Lease change history',
  aco: 'Appointment counter-offers',
  ed: 'Earnings dashboard',
  oc: 'Onboarding call',
  atia: 'AI tenant inquiry agent',
  adl: 'AI-drafted listings',
  ts: 'Tenant screening',
  rf: 'Revenue analytics',
};

const AccountMySubscription = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState(null);

  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'my_sub', email, pwd })
      });
      const result = await response.json();
      if (result.success) {
        setSub(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You can continue using the service until the end of your current billing period.')) return;
    try {
      setCanceling(true);
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'cancel_sub', email, pwd, reason: 'User requested via web' })
      });
      const result = await response.json();
      if (result.success) {
        alert('Subscription cancelled. You can continue using the service until the end of your billing period.');
        fetchSubscription();
      } else {
        alert(result.message || 'Failed to cancel');
      }
    } catch {
      alert('Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatLimit = (val) => {
    if (val === null || val === undefined || val === -1) return 'Unlimited';
    return String(val);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { text: 'text-emerald-400', bg: 'bg-emerald-400/10', dot: 'bg-emerald-400' };
      case 'trial': return { text: 'text-yellow-400', bg: 'bg-yellow-400/10', dot: 'bg-yellow-400' };
      case 'incomplete': return { text: 'text-blue-400', bg: 'bg-blue-400/10', dot: 'bg-blue-400' };
      case 'canceled': case 'cancelled': return { text: 'text-red-400', bg: 'bg-red-400/10', dot: 'bg-red-400' };
      case 'past_due': return { text: 'text-orange-400', bg: 'bg-orange-400/10', dot: 'bg-orange-400' };
      default: return { text: 'text-gray-400', bg: 'bg-gray-400/10', dot: 'bg-gray-400' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'trial': return 'Trial';
      case 'incomplete': return 'Setup Required';
      case 'canceled': case 'cancelled': return 'Cancelled';
      case 'past_due': return 'Past Due';
      default: return status;
    }
  };

  // Progress bar
  const ProgressBar = ({ used, limit, color = 'bg-accent' }) => {
    const isUnlimited = limit === -1;
    const pct = isUnlimited ? 10 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
    const barColor = pct > 80 ? 'bg-orange-400' : color;
    return (
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%`, minWidth: '4px' }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark">
        <AccountHeader title="My Subscription" />
        <div className="flex items-center justify-center p-16">
          <Loader2 className="w-8 h-8 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  if (!sub || !sub.hs) {
    return (
      <div className="min-h-screen bg-dark">
        <AccountHeader title="My Subscription" />
        <div className="flex flex-col items-center justify-center p-16 text-center">
          <CreditCard className="w-14 h-14 text-gray-600 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No Active Subscription</h2>
          <p className="text-gray-500 mb-6 max-w-sm">Subscribe to a plan to start managing your properties with AI-powered automation.</p>
          <button
            onClick={() => navigate('/app/account/plans', { state: { fromPage: 'my-subscription' } })}
            className="px-8 py-3 bg-accent text-dark font-bold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusColor(sub.st);
  const isFree = (sub.cmp || 0) === 0;
  const isInTrial = sub.st === 'trial' && sub.tea;
  const isCanceled = sub.st === 'canceled' || sub.st === 'cancelled';
  const usg = sub.usg || {};
  const card = sub.card;
  const ft = sub.ft || {};
  const teamLimit = ft.tml ?? 1;

  // Build features list — prefer curated display_features from plan if available
  const displayFeatures = sub.df || [];
  const booleanKeys = ['ame', 'ps', 'ad', 'vle', 'lfa', 'cet', 'cb', 'aa', 'dam', 'rr', 'rrs', 'mioc', 'lal', 'aco', 'ed', 'oc', 'atia', 'adl', 'ts', 'rf'];
  const reconstructedFeatures = [];
  if (ft.acm) reconstructedFeatures.push(`${ft.acm === -1 ? 'Unlimited' : ft.acm} AI conversations/month`);
  if (ft.fl && ft.fl > 0) reconstructedFeatures.push(`${ft.fl === -1 ? 'Unlimited' : ft.fl} featured listings`);
  if (ft.er && ft.er !== false) reconstructedFeatures.push(`Export reports (${typeof ft.er === 'string' ? ft.er.toUpperCase().replace(/_/g, ', ') : 'PDF'})`);
  booleanKeys.forEach(k => {
    if (ft[k] === true) reconstructedFeatures.push(featureLabels[k] || k);
  });
  const features = displayFeatures.length > 0 ? displayFeatures : reconstructedFeatures;

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="My Subscription" />
      
      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* YOUR PLAN */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10">
          
          <p className="text-[11px] font-bold text-gray-600 tracking-widest mb-4">YOUR PLAN</p>
          
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-1">{sub.pn}</h2>
              {sub.pd && <p className="text-sm text-gray-500 leading-relaxed">{sub.pd}</p>}
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${statusStyle.bg}`}>
              <div className={`w-2 h-2 rounded-full ${statusStyle.dot}`} />
              <span className={`text-xs font-bold ${statusStyle.text}`}>{getStatusText(sub.st)}</span>
            </div>
          </div>

          {/* Billing */}
          <div className="border-t border-white/5 pt-4">
            <p className="text-[10px] font-bold text-gray-600 tracking-wider mb-2">BILLING</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-accent">
                {isFree ? 'Free' : `$${sub.cmp}`}
              </span>
              <span className="text-gray-500">
                {isFree ? ' — $0.00/month' : '/month'}
              </span>
            </div>
            {!isFree && !isInTrial && !isCanceled && (
              <p className="text-sm text-gray-400 mt-1.5">Next billing: {formatDate(sub.cpe)}</p>
            )}
            {isFree && (
              <p className="text-sm text-gray-400 mt-1.5">No charge — Free plan</p>
            )}
          </div>

          {/* Trial Banner */}
          {isInTrial && (
            <div className="mt-4 flex items-start gap-3 bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-4">
              <Gift className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-yellow-400">Free trial active</p>
                <p className="text-xs text-yellow-400/70 mt-1">Ends {formatDate(sub.tea)} — you won't be charged until then</p>
              </div>
            </div>
          )}

          {/* Cancelled Banner */}
          {isCanceled && (
            <div className="mt-4 flex items-center gap-3 bg-red-400/10 border border-red-400/20 rounded-xl p-4">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-sm text-red-400">Cancelled — access until {formatDate(sub.cpe)}</p>
            </div>
          )}
        </motion.div>

        {/* USAGE */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10">
          
          <p className="text-[11px] font-bold text-gray-600 tracking-widest mb-4">USAGE</p>
          
          {/* Properties */}
          <div className="pb-4 mb-4 border-b border-white/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-base">🏠</span>
                <span className="text-sm text-gray-300 font-medium">Properties</span>
              </div>
              <span className="text-sm font-semibold text-white">{usg.pc || 0} of {formatLimit(usg.pl)}</span>
            </div>
            <ProgressBar used={usg.pc || 0} limit={usg.pl || 0} />
          </div>

          {/* Locations */}
          <div className="pb-4 mb-4 border-b border-white/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-base">📍</span>
                <span className="text-sm text-gray-300 font-medium">Locations</span>
              </div>
              <span className="text-sm font-semibold text-white">{sub.lc || 0} of {formatLimit(sub.ml)}</span>
            </div>
            <ProgressBar used={sub.lc || 0} limit={sub.ml || 0} />
          </div>

          {/* AI Conversations */}
          <div className="pb-4 mb-4 border-b border-white/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-base">🤖</span>
                <span className="text-sm text-gray-300 font-medium">AI Conversations</span>
              </div>
              <span className="text-sm font-semibold text-white">{usg.acu || 0} of {formatLimit(usg.acl)}</span>
            </div>
            <ProgressBar used={usg.acu || 0} limit={usg.acl || 0} />
            <p className="text-[11px] text-gray-600 mt-1.5">Resets each billing cycle</p>
          </div>

          {/* Team Members */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base">👥</span>
                <span className="text-sm text-gray-300 font-medium">Team Members</span>
              </div>
              <span className="text-sm font-semibold text-white">{formatLimit(teamLimit)} allowed</span>
            </div>
          </div>
        </motion.div>

        {/* PAYMENT METHOD */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10">
          
          <p className="text-[11px] font-bold text-gray-600 tracking-widest mb-4">PAYMENT METHOD</p>
          
          {sub.hpm && card ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">{(card.br || '').toUpperCase()} •••• {card.l4}</p>
                <p className="text-xs text-gray-500 mt-0.5">Expires {card.em}/{card.ey}</p>
              </div>
              <button
                onClick={() => navigate('/app/account/payment', { state: { fromPage: 'my-subscription' } })}
                className="px-4 py-2 bg-dark-200 rounded-lg text-sm font-semibold text-accent hover:bg-dark-200/80 transition-colors"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-400/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">No payment method</p>
                <p className="text-xs text-yellow-400 mt-0.5">Required to continue after trial</p>
              </div>
              <button
                onClick={() => navigate('/app/account/payment', { state: { fromPage: 'my-subscription' } })}
                className="px-4 py-2 bg-accent rounded-lg text-sm font-bold text-dark hover:shadow-lg hover:shadow-accent/30 transition-all"
              >
                Add
              </button>
            </div>
          )}
        </motion.div>

        {/* PLAN FEATURES */}
        {features.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-dark-100 rounded-2xl p-5 border border-white/10">
            
            <p className="text-[11px] font-bold text-gray-600 tracking-widest mb-4">PLAN FEATURES</p>
            
            <div className="space-y-3">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-accent shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ACTIONS */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="space-y-3">
          
          <button
            onClick={() => navigate('/app/account/plans', { state: { fromPage: 'my-subscription' } })}
            className="w-full flex items-center justify-center gap-2 py-4 bg-accent text-dark font-bold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all"
          >
            <ChevronUp className="w-5 h-5" />
            Upgrade Plan
          </button>

          {!isCanceled && (
            <button
              onClick={handleCancel}
              disabled={canceling}
              className="w-full flex items-center justify-center gap-2 py-3.5 border border-red-400/30 rounded-xl text-red-400 font-semibold hover:bg-red-400/5 transition-colors disabled:opacity-50"
            >
              {canceling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Cancel Subscription
                </>
              )}
            </button>
          )}
        </motion.div>

        {/* HELP */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10 mb-8">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-gray-600 mt-0.5 shrink-0" />
            <p className="text-sm text-gray-500 leading-relaxed">
              Questions? Contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountMySubscription;
